import { useCallback, useEffect, useRef } from "react";
import { useFetcher } from "react-router-dom";
import { FIRST_TRACK_ACTIVITY_DELAY_MS, TRACK_ACTIVITY_INTERVAL_MS, shouldSendActivityPing, submitActivityPingSafely } from "@/utils/activity-tracker";
import { DataService } from "@/services/data";

const TRACKED_EVENTS: Array<keyof WindowEventMap> = ["mousemove", "click", "keydown", "scroll"];

export const useActivityTracker = (enabled: boolean) => {
    const fetcher = useFetcher();
    const hadRealActivityRef = useRef(false);
    const inFlightRef = useRef(false);

    const markRealActivity = useCallback(() => {
        hadRealActivityRef.current = true;
    }, []);

    useEffect(() => {
        if (!enabled) return;

        TRACKED_EVENTS.forEach(eventName => {
            window.addEventListener(eventName, markRealActivity, { passive: true });
        });

        return () => {
            TRACKED_EVENTS.forEach(eventName => {
                window.removeEventListener(eventName, markRealActivity);
            });
        };
    }, [enabled, markRealActivity]);

    useEffect(() => {
        if (fetcher.state === "idle") {
            inFlightRef.current = false;
        }
    }, [fetcher.state]);

    const tryTrackActivity = useCallback(() => {
        const shouldTrack = shouldSendActivityPing({
            enabled,
            hadRealActivity: hadRealActivityRef.current,
            inFlight: inFlightRef.current
        });
        if (!shouldTrack) return;

        const submitted = submitActivityPingSafely(() => {
            inFlightRef.current = true;
            DataService.client
                .url("/users/activities/track/")
                .post({ name: "crm_user_activity" })
                .res(() => {})
                .finally(() => inFlightRef.current = false);
        });

        if (submitted) {
            hadRealActivityRef.current = false;
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;

        const firstTickTimeout = window.setTimeout(() => {
            tryTrackActivity();
        }, FIRST_TRACK_ACTIVITY_DELAY_MS);
        const interval = window.setInterval(tryTrackActivity, TRACK_ACTIVITY_INTERVAL_MS);

        return () => {
            window.clearTimeout(firstTickTimeout);
            window.clearInterval(interval);
        };
    }, [enabled, tryTrackActivity]);
};
